import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ClipboardList, User, X, Check } from 'lucide-react';

/**
 * CriarTarefaModal — modal para criar uma tarefa na agenda.
 *
 * Props:
 *   onClose            () => void
 *   onSaved            () => void  (chamado após salvar com sucesso)
 *   leadIdInicial      number | null   (pré-seleciona cliente)
 *   clienteNomeInicial string          (nome exibido no campo cliente)
 */
export default function CriarTarefaModal({
  onClose,
  onSaved,
  leadIdInicial = null,
  clienteNomeInicial = '',
}) {
  const { isChefe, isGerenteComercial } = useAuth();
  const isPowerUser = isChefe() || isGerenteComercial();

  // ── Campos do formulário ───────────────────────────────────────────────────
  const [nome, setNome]       = useState('');
  const [dataHora, setDataHora] = useState('');
  const [leadId, setLeadId]   = useState(leadIdInicial);

  // Autocomplete Cliente
  const [clienteQuery, setClienteQuery]     = useState(clienteNomeInicial);
  const [clienteResults, setClienteResults] = useState([]);
  const [leads, setLeads]                   = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(!!leadIdInicial);
  const clienteRef = useRef(null);

  // Autocomplete Funcionário (chefe/GC)
  const [funcQuery, setFuncQuery]         = useState('');
  const [funcResults, setFuncResults]     = useState([]);
  const [funcionarios, setFuncionarios]   = useState([]);
  const [paraUsuarioId, setParaUsuarioId] = useState(null);
  const [funcNome, setFuncNome]           = useState('');
  const [paraMim, setParaMim]             = useState(true); // default: para si mesmo
  const funcRef = useRef(null);

  const [salvando, setSalvando] = useState(false);
  const [erro, setErro]         = useState('');

  // ── Carregar dados ─────────────────────────────────────────────────────────
  useEffect(() => {
    api.get('/leads/').then(r => setLeads(r.data)).catch(() => {});
    if (isPowerUser) {
      api.get('/agenda/funcionarios-tarefa/').then(r => setFuncionarios(r.data)).catch(() => {});
    }
  }, []); // eslint-disable-line

  // Fecha dropdowns ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (clienteRef.current && !clienteRef.current.contains(e.target)) setClienteResults([]);
      if (funcRef.current && !funcRef.current.contains(e.target)) setFuncResults([]);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Autocomplete handlers ──────────────────────────────────────────────────
  const handleClienteQuery = (q) => {
    setClienteQuery(q);
    setLeadId(null);
    setClienteSelecionado(false);
    if (!q.trim()) { setClienteResults([]); return; }
    const ql = q.toLowerCase();
    setClienteResults(leads.filter(l => (l.cliente_nome || '').toLowerCase().includes(ql)).slice(0, 8));
  };

  const selecionarCliente = (lead) => {
    setLeadId(lead.id);
    setClienteQuery(lead.cliente_nome || '');
    setClienteSelecionado(true);
    setClienteResults([]);
  };

  const limparCliente = () => {
    setLeadId(null);
    setClienteQuery('');
    setClienteSelecionado(false);
    setClienteResults([]);
  };

  const handleFuncQuery = (q) => {
    setFuncQuery(q);
    setParaUsuarioId(null);
    setFuncNome('');
    if (!q.trim()) { setFuncResults([]); return; }
    const ql = q.toLowerCase();
    setFuncResults(funcionarios.filter(f => f.nome.toLowerCase().includes(ql)).slice(0, 8));
  };

  const selecionarFuncionario = (f) => {
    setParaUsuarioId(f.id);
    setFuncNome(f.nome);
    setFuncQuery(f.nome);
    setFuncResults([]);
    setParaMim(false);
  };

  const handleParaMim = () => {
    setParaMim(true);
    setParaUsuarioId(null);
    setFuncNome('');
    setFuncQuery('');
    setFuncResults([]);
  };

  // ── Salvar ────────────────────────────────────────────────────────────────
  const salvar = async () => {
    if (!nome.trim()) { setErro('Informe o nome da tarefa.'); return; }
    if (!dataHora)    { setErro('Informe a data da tarefa.'); return; }
    setSalvando(true);
    setErro('');
    try {
      const body = {
        titulo: nome.trim().slice(0, 200),
        tipo: 'tarefa',
        data_hora: dataHora,
        concluido: false,
      };
      if (leadId) body.lead = leadId;
      if (!paraMim && paraUsuarioId && isPowerUser) body.para_usuario_id = paraUsuarioId;
      await api.post('/agenda/', body);
      onSaved?.();
      onClose();
    } catch {
      setErro('Erro ao salvar. Tente novamente.');
      setSalvando(false);
    }
  };

  // ── Estilos ────────────────────────────────────────────────────────────────
  const S = {
    overlay: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    box: {
      background: '#111827', border: '1px solid rgba(246,201,14,0.25)',
      borderRadius: 14, padding: 28, width: 440, maxWidth: '95vw',
      maxHeight: '90vh', overflowY: 'auto',
    },
    title: {
      fontSize: 16, fontWeight: 700, color: '#f6c90e', marginBottom: 20,
      display: 'flex', alignItems: 'center', gap: 8,
    },
    label: { fontSize: 12, color: '#94a3b8', marginBottom: 5, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
    input: {
      width: '100%', background: '#0a0f1a', border: '1px solid #1a2035',
      borderRadius: 8, padding: '9px 12px', color: '#e2e8f0', fontSize: 14,
      outline: 'none', boxSizing: 'border-box', marginBottom: 16,
    },
    group: { marginBottom: 4, position: 'relative' },
    dropdown: {
      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
      background: '#0e1420', border: '1px solid #1a2035', borderRadius: 8,
      boxShadow: '0 8px 24px rgba(0,0,0,0.5)', maxHeight: 200, overflowY: 'auto',
    },
    dropItem: {
      padding: '9px 14px', cursor: 'pointer', fontSize: 13, color: '#cbd5e1',
      borderBottom: '1px solid #1a2035',
    },
    charCount: { fontSize: 11, color: '#475569', textAlign: 'right', marginTop: -12, marginBottom: 12 },
    errBox: {
      background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)',
      borderRadius: 8, padding: '8px 12px', color: '#f87171', fontSize: 13, marginBottom: 14,
    },
    row: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 },
    btnParaMim: (ativo) => ({
      padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
      border: ativo ? '1px solid rgba(246,201,14,0.5)' : '1px solid #1a2035',
      background: ativo ? 'rgba(246,201,14,0.12)' : 'transparent',
      color: ativo ? '#f6c90e' : '#64748b',
      whiteSpace: 'nowrap', flexShrink: 0,
    }),
    footer: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 },
    btnCancelar: {
      padding: '9px 18px', borderRadius: 8, border: '1px solid #1a2035',
      background: 'transparent', color: '#64748b', fontSize: 13, cursor: 'pointer',
    },
    btnSalvar: (dis) => ({
      padding: '9px 22px', borderRadius: 8, border: 'none',
      background: dis ? '#1a2035' : 'rgba(74,222,128,0.15)',
      color: dis ? '#475569' : '#4ade80',
      fontSize: 13, fontWeight: 700, cursor: dis ? 'not-allowed' : 'pointer',
      display: 'flex', alignItems: 'center', gap: 6,
    }),
  };

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.box}>
        <div style={S.title}>
          <ClipboardList size={16} />
          Criar Nova Tarefa
        </div>

        {erro && <div style={S.errBox}>{erro}</div>}

        {/* Nome */}
        <div style={S.group}>
          <label style={S.label}>Nome da Tarefa</label>
          <input
            style={S.input}
            maxLength={200}
            placeholder="Ex.: Ligar para cliente, Enviar proposta..."
            value={nome}
            onChange={e => setNome(e.target.value)}
            autoFocus
          />
          <div style={S.charCount}>{nome.length}/200</div>
        </div>

        {/* Data */}
        <div style={S.group}>
          <label style={S.label}>Data e Hora</label>
          <input
            style={S.input}
            type="datetime-local"
            value={dataHora}
            onChange={e => setDataHora(e.target.value)}
          />
        </div>

        {/* Cliente (autocomplete) */}
        <div style={S.group} ref={clienteRef}>
          <label style={S.label}>Cliente (opcional)</label>
          <div style={{ position: 'relative' }}>
            <input
              style={{ ...S.input, paddingRight: leadId ? 36 : 12 }}
              placeholder="Digite para buscar um cliente..."
              value={clienteQuery}
              onChange={e => handleClienteQuery(e.target.value)}
              onFocus={() => { if (clienteQuery.trim() && !clienteSelecionado) handleClienteQuery(clienteQuery); }}
            />
            {leadId && (
              <button
                onClick={limparCliente}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 2 }}
              >
                <X size={14} />
              </button>
            )}
          </div>
          {clienteResults.length > 0 && (
            <div style={S.dropdown}>
              {clienteResults.map(l => (
                <div
                  key={l.id}
                  style={S.dropItem}
                  onMouseDown={() => selecionarCliente(l)}
                  onMouseEnter={e => e.currentTarget.style.background = '#1a2035'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {l.cliente_nome}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Para quem (chefe/GC) */}
        {isPowerUser && (
          <div style={{ marginBottom: 16 }}>
            <label style={S.label}>Para quem</label>
            <div style={S.row}>
              <div style={{ flex: 1, position: 'relative' }} ref={funcRef}>
                <input
                  style={{ ...S.input, marginBottom: 0 }}
                  placeholder="Buscar funcionário..."
                  value={paraMim ? '' : funcQuery}
                  onChange={e => { setParaMim(false); handleFuncQuery(e.target.value); }}
                  onFocus={() => {
                    if (paraMim) setParaMim(false);
                    if (!funcQuery.trim()) setFuncResults(funcionarios.slice(0, 20));
                  }}
                />
                {funcResults.length > 0 && (
                  <div style={S.dropdown}>
                    {funcResults.map(f => (
                      <div
                        key={f.id}
                        style={S.dropItem}
                        onMouseDown={() => selecionarFuncionario(f)}
                        onMouseEnter={e => e.currentTarget.style.background = '#1a2035'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <span>{f.nome}</span>
                        {f.cargo && <span style={{ fontSize: 11, color: '#64748b', marginLeft: 6 }}>· {f.cargo}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button style={S.btnParaMim(paraMim)} onClick={handleParaMim}>
                <User size={12} style={{ display: 'inline', marginRight: 4 }} />
                Para mim
              </button>
            </div>
            {!paraMim && funcNome && (
              <div style={{ fontSize: 12, color: '#60a5fa', marginTop: -8 }}>
                Tarefa será criada para: <strong>{funcNome}</strong>
              </div>
            )}
          </div>
        )}

        <div style={S.footer}>
          <button style={S.btnCancelar} onClick={onClose}>Cancelar</button>
          <button
            style={S.btnSalvar(salvando || !nome.trim() || !dataHora)}
            onClick={salvar}
            disabled={salvando || !nome.trim() || !dataHora}
          >
            <Check size={14} />
            {salvando ? 'Salvando...' : 'Criar Tarefa'}
          </button>
        </div>
      </div>
    </div>
  );
}
